import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { DollarSign, FileText, Plus, Download, Check, Clock, AlertTriangle, XCircle, Building2, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_STYLES = {
  paid: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
  pending: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  overdue: 'bg-red-500/20 text-red-400 border-red-500/30',
  cancelled: 'bg-slate-500/20 text-slate-400 border-slate-500/30',
};

export default function SuperAdminBilling() {
  const [invoices, setInvoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [generateOpen, setGenerateOpen] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState('');

  // eslint-disable-next-line no-unused-vars
  const token = localStorage.getItem('superAdminToken');
  const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('superAdminToken')}` });

  const fetchAll = useCallback(async () => {
    try {
      const [invRes, sumRes, tenRes] = await Promise.all([
        axios.get(`${API}/platform/billing/invoices`, { headers: getHeaders() }),
        axios.get(`${API}/platform/billing/summary`, { headers: getHeaders() }),
        axios.get(`${API}/platform/tenants`, { headers: getHeaders() }),
      ]);
      setInvoices(invRes.data);
      setSummary(sumRes.data);
      setTenants(tenRes.data);
    } catch { toast.error('Failed to load billing data'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const generateInvoice = async () => {
    if (!selectedTenant) { toast.error('Select a tenant'); return; }
    try {
      await axios.post(`${API}/platform/billing/invoices/generate?tenant_id=${selectedTenant}`, {}, { headers: getHeaders() });
      toast.success('Invoice generated');
      setGenerateOpen(false);
      setSelectedTenant('');
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to generate invoice'); }
  };

  const updateStatus = async (invoiceId, status) => {
    try {
      await axios.patch(`${API}/platform/billing/invoices/${invoiceId}?status=${status}`, {}, { headers: getHeaders() });
      toast.success(`Invoice marked as ${status}`);
      fetchAll();
    } catch { toast.error('Failed to update status'); }
  };

  const filtered = filter === 'all' ? invoices : invoices.filter(i => i.status === filter);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-500" /></div>;

  return (
    <div className="space-y-6" data-testid="billing-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold text-white tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>Billing & Invoices</h1>
          <p className="text-slate-400 mt-1">Manage invoices, track revenue, and billing lifecycle</p>
        </div>
        <Dialog open={generateOpen} onOpenChange={setGenerateOpen}>
          <DialogTrigger asChild>
            <Button className="bg-violet-600 hover:bg-violet-700 text-white font-bold" data-testid="generate-invoice-btn">
              <Plus size={14} className="mr-2" />Generate Invoice
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-slate-900 border-slate-700">
            <DialogHeader>
              <DialogTitle className="text-white">Generate Invoice</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <Select value={selectedTenant} onValueChange={setSelectedTenant}>
                <SelectTrigger className="bg-slate-800 border-slate-600 text-white" data-testid="select-tenant-invoice">
                  <SelectValue placeholder="Select tenant..." />
                </SelectTrigger>
                <SelectContent>
                  {tenants.filter(t => t.status === 'active' && t.plan !== 'free').map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name} ({t.plan})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={generateInvoice} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold" data-testid="confirm-generate-btn">
                Generate Invoice
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Total Revenue</p>
                  <p className="text-2xl font-extrabold text-emerald-400 mt-1" data-testid="total-revenue">${summary.total_revenue?.toLocaleString()}</p>
                </div>
                <DollarSign size={24} className="text-emerald-500/50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Pending</p>
                  <p className="text-2xl font-extrabold text-amber-400 mt-1" data-testid="total-pending">${summary.total_pending?.toLocaleString()}</p>
                </div>
                <Clock size={24} className="text-amber-500/50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Overdue</p>
                  <p className="text-2xl font-extrabold text-red-400 mt-1" data-testid="total-overdue">${summary.total_overdue?.toLocaleString()}</p>
                </div>
                <AlertTriangle size={24} className="text-red-500/50" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Total Invoices</p>
                  <p className="text-2xl font-extrabold text-white mt-1" data-testid="total-invoices">{summary.total_invoices}</p>
                </div>
                <FileText size={24} className="text-slate-500/50" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filter & Table */}
      <Card className="bg-slate-800/50 border-slate-700">
        <CardHeader className="pb-3 flex-row items-center justify-between">
          <CardTitle className="text-white">Invoices</CardTitle>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-40 bg-slate-900 border-slate-600 text-white" data-testid="invoice-filter">
              <Filter size={14} className="mr-2" /><SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">All Status</SelectItem>
              <SelectItem value="paid" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Paid</SelectItem>
              <SelectItem value="pending" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Pending</SelectItem>
              <SelectItem value="overdue" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Overdue</SelectItem>
              <SelectItem value="cancelled" className="text-white hover:bg-slate-700 focus:bg-slate-700 focus:text-white">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow className="border-slate-700">
                <TableHead className="text-slate-400 font-bold">Invoice #</TableHead>
                <TableHead className="text-slate-400 font-bold">Tenant</TableHead>
                <TableHead className="text-slate-400 font-bold">Plan</TableHead>
                <TableHead className="text-slate-400 font-bold">Amount</TableHead>
                <TableHead className="text-slate-400 font-bold">Status</TableHead>
                <TableHead className="text-slate-400 font-bold">Date</TableHead>
                <TableHead className="text-slate-400 font-bold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length > 0 ? filtered.map(inv => (
                <TableRow key={inv.id} className="border-slate-700 hover:bg-slate-800/50" data-testid={`invoice-${inv.id}`}>
                  <TableCell className="text-white font-mono text-xs">{inv.invoice_number}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Building2 size={14} className="text-slate-500" />
                      <span className="text-white text-sm">{inv.tenant_name}</span>
                    </div>
                  </TableCell>
                  <TableCell><Badge variant="outline" className="border-violet-500/50 text-violet-400 text-xs">{inv.plan}</Badge></TableCell>
                  <TableCell className="text-white font-semibold">{inv.currency === 'INR' ? '₹' : '$'}{inv.amount?.toLocaleString()}</TableCell>
                  <TableCell><Badge className={`${STATUS_STYLES[inv.status]} border text-xs`}>{inv.status}</Badge></TableCell>
                  <TableCell className="text-slate-400 text-xs">{inv.created_at?.slice(0, 10)}</TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      {inv.status === 'pending' && (
                        <>
                          <Button size="sm" variant="ghost" className="text-emerald-400 hover:text-emerald-300 h-7 text-xs" onClick={() => updateStatus(inv.id, 'paid')} data-testid={`mark-paid-${inv.id}`}>
                            <Check size={12} className="mr-1" />Paid
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300 h-7 text-xs" onClick={() => updateStatus(inv.id, 'overdue')}>
                            Overdue
                          </Button>
                        </>
                      )}
                      {inv.status === 'overdue' && (
                        <Button size="sm" variant="ghost" className="text-emerald-400 hover:text-emerald-300 h-7 text-xs" onClick={() => updateStatus(inv.id, 'paid')}>
                          <Check size={12} className="mr-1" />Mark Paid
                        </Button>
                      )}
                      {(inv.status === 'pending' || inv.status === 'overdue') && (
                        <Button size="sm" variant="ghost" className="text-slate-400 hover:text-slate-300 h-7 text-xs" onClick={() => updateStatus(inv.id, 'cancelled')}>
                          <XCircle size={12} className="mr-1" />Cancel
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )) : (
                <TableRow><TableCell colSpan={7} className="text-center text-slate-500 py-8">No invoices found. Generate your first invoice to get started.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
